import { ChevronDownIcon, ChevronUpIcon, EyeIcon, ListBulletIcon } from '@heroicons/react/24/outline'
import { BaseFile, IS_COMPANY_KIRK } from 'config'
import {
  AiModelSelection,
  AiModelsOptions,
  getDefaultAiModelOptionIndex,
  resolveDefaultAiSelection,
} from 'config/ai.type'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getReviewFormAiAutoFill, postReviewFormAiAutoFill, postReviewFormAiAutoFillFields } from 'services'
import { openS3Document } from 'services/apis/documents'
import { Modal, PlainTable, Tooltip } from 'stories/components'
import { type DocumentFile, InputFileTable } from 'stories/components/InputFileTable/InputFileTable'
import { formatTime } from 'utils'
import { confirmOptions } from 'utils/modals/confirmOptions'

import { AIAutoFillFieldModal } from './AIAutoFillFieldModal'

interface AIAutoFillModalProps {
  id: number
  onClose: (success: boolean, fields?: Record<string, any> | null) => void
}

enum ReviewFormAutofillStatus {
  Success = 'success',
  Failed = 'failed',
  Pending = 'pending',
}

export interface IReviewFormAiAutoFillData {
  id: number
  loanNumber: number
  reviewFormId: number
  files: BaseFile[]
  data: { question: string; answer: string }[]
  fields: Record<string, any>
  status: ReviewFormAutofillStatus
  createdBy: string
  createdAt: string
}

export const AIAutoFillModal = ({ id, onClose }: AIAutoFillModalProps) => {
  const [files, setFiles] = useState<DocumentFile[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showHistory, setShowHistory] = useState(true)

  const [aiAutoFillData, setAiAutoFillData] = useState<IReviewFormAiAutoFillData[]>([])
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<IReviewFormAiAutoFillData | null>(null)
  const [autoFillFormData, setAutoFillFormData] = useState<Record<string, any> | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [refreshAfterClose, setRefreshAfterClose] = useState(false)

  useEffect(() => {
    fetchAiAutoFillData()
  }, [])

  const fetchAiAutoFillData = async () => {
    setIsLoading(true)
    const data = await getReviewFormAiAutoFill(id)
    setAiAutoFillData(data)
    setIsLoading(false)
  }

  const historyHeader = ['No', 'File Name', 'Uploaded At', 'Status', 'Action']

  const historyData = useMemo(() => {
    return aiAutoFillData.map((item, index) => [
      index + 1,
      <div className="flex flex-wrap gap-1">
        {item.files.map((file, fIndex) => (
          <span key={fIndex}>
            <span className="btn-link" onClick={() => openS3Document(file.fileKey, true)}>
              {file.name}
            </span>
            {fIndex < item.files.length - 1 && ', '}
          </span>
        ))}
      </div>,
      formatTime(item.createdAt),
      <span className="capitalize">{item.status}</span>,
      <div className="flex">
        <Tooltip message="View Question / Answers">
          <span className="btn-icon flex" onClick={() => setSelectedHistoryItem(item)}>
            <EyeIcon className="w-4 h-4" />
          </span>
        </Tooltip>
        <Tooltip message="View Auto Fill Fields">
          <span className="btn-icon flex" onClick={() => setAutoFillFormData(item.fields)}>
            <ListBulletIcon className="w-4 h-4" />
          </span>
        </Tooltip>
      </div>,
    ])
  }, [aiAutoFillData])

  const { hasPermission } = usePermissions()
  const loanGlobalSettings = useSelector((state: any) => state.loanDetail.loanGlobalSettings)
  const defaultAiSelection = resolveDefaultAiSelection(loanGlobalSettings)

  const onSubmit = async () => {
    if (files.length === 0) return

    let aiSelection: AiModelSelection | undefined
    const canChangeModel = IS_COMPANY_KIRK && hasPermission('Global.Ai.Model.Change')
    if (canChangeModel) {
      const selection = (await confirmOptions(
        'Choose AI Model',
        AiModelsOptions,
        getDefaultAiModelOptionIndex(defaultAiSelection.engine, defaultAiSelection.model),
      )) as unknown
      if (!selection) return
      aiSelection = selection as AiModelSelection
    }

    setIsLoading(true)
    try {
      const { success, data } = await postReviewFormAiAutoFill(id, files, {
        model: aiSelection?.model,
        engine: aiSelection?.engine,
      })
      if (success) {
        const { formData } = data
        setAutoFillFormData(formData)
        setIsSubmitted(true)
        setRefreshAfterClose(true)
      }
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Modal
        title="AI Review"
        titleOkay="Submit"
        titleCancel="Close"
        isOpen
        lastUpdatedAt={Date.now()}
        loading={isLoading}
        onClose={() => onClose(refreshAfterClose)}
        onOk={onSubmit}
        disabled={files.length === 0}
      >
        <div className="w-144">
          <InputFileTable
            title="Upload Document for AI"
            name="ai-autofill-file"
            filePath=""
            acceptFileTypes="application/pdf"
            multiple
            value={files}
            onChange={setFiles}
            showStatus={false}
            showCategory={false}
          />

          <div className="mt-6">
            <button
              className="flex items-center gap-1 text-sm font-semibold cursor-pointer hover:text-shade-blue"
              onClick={() => setShowHistory(!showHistory)}
            >
              Upload History
              {showHistory ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
            </button>
            {showHistory && (
              <div className="mt-2">
                <PlainTable header={historyHeader} data={historyData} tdClass="px-4 py-2 whitespace-nowrap" />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {!!selectedHistoryItem && (
        <Modal title="AutoFill Details" isOpen onClose={() => setSelectedHistoryItem(null)} titleCancel="" titleOkay="">
          <div className="w-196 text-sm">
            {selectedHistoryItem.data.map((item, index) => (
              <div className="py-2 border-b">
                <p className="pb-2">
                  {index + 1}. {item.question}
                </p>
                <p className="text-gray-500 pl-4">{item.answer}</p>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {!!autoFillFormData && (
        <AIAutoFillFieldModal
          formData={autoFillFormData}
          onClose={async (acceptedValues) => {
            setAutoFillFormData(null)
            if (acceptedValues) {
              setIsLoading(true)
              await postReviewFormAiAutoFillFields(id, acceptedValues)
              setIsLoading(false)
              setRefreshAfterClose(true)
            }
            if (isSubmitted) onClose(true)
          }}
        />
      )}
    </>
  )
}
